import type {ReactElement} from 'react';
import {useMemo, useState} from 'react';
import type {CheckoutHostContextValue, CheckoutHostProviderProps, CheckoutRequest} from '@Components/checkout-flow/checkout-flow.types';
import {CHECKOUT_HOST_PANEL_ID_SUFFIX} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutFlowPanel} from '@Components/checkout-flow/checkout-flow';
import {checkoutHostContext} from '@Components/checkout-flow/checkout-host.context';

/**
 * Lets a modal swap its own content for the checkout flow in place: the host content stays mounted but hidden
 * while the flow runs, so the user's selections survive a cancelled checkout.
 */
export function CheckoutHostProvider({modalId, panelId, onCheckoutExit, children}: CheckoutHostProviderProps): ReactElement {
  const [request, setRequest] = useState<CheckoutRequest | null>(null);

  const contextValue = useMemo<CheckoutHostContextValue>(() => {
    return {
      startCheckout: (checkoutRequest: CheckoutRequest): void => {
        setRequest(checkoutRequest);
      },
    };
  }, []);

  const onExit = (): void => {
    setRequest(null);
    onCheckoutExit?.();
  };

  const getCheckoutFlow = (): ReactElement | null => {
    if (request === null) {
      return null;
    }
    return <CheckoutFlowPanel request={request} panelId={`${panelId}${CHECKOUT_HOST_PANEL_ID_SUFFIX}`} modalId={modalId} onExit={onExit} />;
  };

  return (
    <checkoutHostContext.Provider value={contextValue}>
      {getCheckoutFlow()}
      <div hidden={request !== null} className="_full-height">
        {children}
      </div>
    </checkoutHostContext.Provider>
  );
}
